import { z } from 'zod/v4';

export const GetTracingNodeParamsSchema = z.object({
  id: z.string().min(1),
});

/** A node of the tracing graph — a company facility at one step of a chain (`tracing_nodes`). */
export const TracingNodeSchema = z.object({
  id: z.string(),
  /** The company (or other asset) at the node. */
  assetId: z.string(),
  assetType: z.string(),
  assetCountry: z.string().nullable(),
  assetCountryName: z.string().nullable(),
  productionProcess: z.string().nullable(),
  productionProcessName: z.string().nullable(),
  featuredFacilityProcess: z.string().nullable(),
  featuredFacilityProcessName: z.string().nullable(),
  /** The node's facility process ids; `null` when none were stored. */
  facilityProcesses: z.array(z.string()).nullable(),
});

export type TracingNode = z.infer<typeof TracingNodeSchema>;

/** Wire shape: the standard `reply.apiSuccess` envelope around the node. */
export const GetTracingNodeResponseSchema = z.object({
  metadata: z.object({ success: z.literal(true) }),
  data: TracingNodeSchema,
});

export type GetTracingNodeResponse = z.infer<
  typeof GetTracingNodeResponseSchema
>;

/** The order a node is read in — `GET /transactions/node/:id`. */
export const GetTracingNodeDetailQuerySchema = z.object({
  orderId: z.string().min(1),
  type: z.enum(['PURCHASE_ORDER', 'SALES_ORDER']),
});

export type GetTracingNodeDetailQuery = z.infer<
  typeof GetTracingNodeDetailQuerySchema
>;

/** The company at a node, as the node details show it. */
export const TracingNodeCompanySchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  officialName: z.string().nullable(),
  country: z.string().nullable(),
  city: z.string().nullable(),
  featuredImageFileUrl: z.string().nullable(),
  /** The company's location as a GeoJSON object; `null` when none (or unreadable). */
  geojson: z.unknown().nullable(),
});

export type TracingNodeCompany = z.infer<typeof TracingNodeCompanySchema>;

/** A document attached to a node. */
export const TracingNodeDocumentSchema = z.object({
  id: z.string(),
  tracingNodeId: z.string(),
  name: z.string().nullable(),
  reference: z.string().nullable(),
  description: z.string().nullable(),
  type: z.string().nullable(),
  fileId: z.string().nullable(),
  fileName: z.string().nullable(),
  fileType: z.string().nullable(),
  fileContentType: z.string().nullable(),
  /** A read-only signed download link, valid for an hour. */
  fileUrl: z.string().nullable(),
  /** ISO 8601. */
  createdAt: z.string().nullable(),
  createdByCompanyId: z.string().nullable(),
  createdByCompanyName: z.string().nullable(),
});

export type TracingNodeDocument = z.infer<typeof TracingNodeDocumentSchema>;

const TracingOrderPartySchema = z
  .object({ id: z.string(), name: z.string().nullable() })
  .nullable();

/** The purchase or sales order a transaction carries. */
export const TracingTransactionOrderSchema = z.object({
  id: z.string(),
  orderNumber: z.string().nullable(),
  tracingOrderId: z.string().nullable(),
  tracingAttachmentTemplateId: z.string().nullable(),
  docCompletionPercentage: z.number().nullable(),
  buyerCompanyId: z.string().nullable(),
  supplierCompanyId: z.string().nullable(),
  receiverCompanyId: z.string().nullable(),
  buyerCompany: TracingOrderPartySchema,
  supplierCompany: TracingOrderPartySchema,
  receiverCompany: TracingOrderPartySchema,
  lines: z.array(
    z.object({
      id: z.string(),
      quantity: z.number().nullable(),
      unit: z.string().nullable(),
      product: z
        .object({ id: z.string(), name: z.string().nullable() })
        .nullable(),
    }),
  ),
});

export type TracingTransactionOrder = z.infer<
  typeof TracingTransactionOrderSchema
>;

/** A transaction on one of the trace's links at a node. */
export const TracingNodeTransactionSchema = z.object({
  id: z.string(),
  tracingLinkId: z.string(),
  originTracingNodeId: z.string(),
  destinationTracingNodeId: z.string(),
  assetType: z.string(),
  assetId: z.string(),
  transactionType: z.string(),
  /** Whether the transaction links an asset (an order, a company …) to the link. */
  isLinkingAsset: z.boolean(),
  /** ISO 8601. */
  createdAt: z.string().nullable(),
  createdByCompanyId: z.string().nullable(),
  /** The order, for purchase- and sales-order transactions; `null` otherwise. */
  asset: TracingTransactionOrderSchema.nullable(),
});

export type TracingNodeTransaction = z.infer<
  typeof TracingNodeTransactionSchema
>;

/** A node of an order's trace with its company, documents and transactions. */
export const TracingNodeDetailSchema = TracingNodeSchema.extend({
  /** The company at the node; `null` for a node that is not a company. */
  asset: TracingNodeCompanySchema.nullable(),
  documents: z.array(TracingNodeDocumentSchema),
  /** Newest first. */
  transactions: z.array(TracingNodeTransactionSchema),
});

export type TracingNodeDetail = z.infer<typeof TracingNodeDetailSchema>;

/** Wire shape: the standard `reply.apiSuccess` envelope around the node. */
export const GetTracingNodeDetailResponseSchema = z.object({
  metadata: z.object({ success: z.literal(true) }),
  data: TracingNodeDetailSchema,
});

export type GetTracingNodeDetailResponse = z.infer<
  typeof GetTracingNodeDetailResponseSchema
>;

/**
 * `GET /api/cascade-tracing/transactions/tracing-node-product-trace/:id`: the
 * node, its company (`asset`) and the `products` it produces — each an
 * enriched style with its order line's quantities, the node it comes from
 * and the upstream `inputProducts` that can feed it. Products carry the
 * style's open-ended enrichment, so their fields are not closed.
 */
export const NodeProductTraceSchema = z
  .object({
    id: z.string(),
    products: z.array(z.object({}).catchall(z.unknown())),
  })
  .catchall(z.unknown());
